import { Injectable } from '@angular/core';

import { BlockerModalOptions, ConfirmationModalPopupOptions, InformationModalPopupOptions } from '@app/models/modals';

import { DialogService } from '@app/modules/ui/dialog/dialog.service';
import { Transition } from '@uirouter/angular';
import defaults from 'lodash-es/defaults';
import { map, take } from 'rxjs/operators';
import { ConfirmationModalComponent } from './confirmation-modal.component';
import { InformationModalComponent } from './information-modal.component';
import { BlockerModalComponent } from './blocker-modal.component';

@Injectable()
export class ModalsService {
  private ignoreNextNavigationModalOpening = false;
  private defaults = {
    popupOptions: {
      confirmation: {
        descriptionKey: 'modals.confirmation.description',
        descriptionValues: {},
        titleKey: 'modals.confirmation.title',
        agreeButtonKey: 'modals.confirmation.yes',
        agreeButtonClasses: 'btn __l',
        declineButtonKey: 'modals.confirmation.no'
      },
      error: {
        descriptionKey: 'modals.error.description',
        descriptionValues: {},
        titleKey: 'modals.error.title',
        agreeButtonKey: 'modals.error.ok',
        agreeButtonClasses: 'btn __l'
      },
      information: {
        descriptionKey: 'modals.information.description',
        descriptionValues: {},
        titleKey: 'modals.information.title',
        confirmButtonKey: 'modals.information.ok',
        confirmButtonClasses: 'btn __l'
      },
      blocker: {
        headerText: 'Missing Header',
        BodyText: 'Missing Body Text'
      }
    }
  };

  constructor(private dialog: DialogService) {}

  /**
   * Opens an information modal with no options, just OK button
   * @param popupOptions Popup options
   */
  openInformationModal(popupOptions?: Partial<InformationModalPopupOptions>) {
    const options = popupOptions || {};
    defaults(options, this.defaults.popupOptions.information);

    return this.dialog.open(InformationModalComponent, { data: options });
  }

  /**
   * Opens a blocking modal with no options and no buttons which cannot be dismissed
   * @param popupOptions Popup options
   */
  openBlockerModal(popupOptions?: Partial<BlockerModalOptions>) {
    const options = popupOptions || {};
    defaults(options, this.defaults.popupOptions.blocker);

    return this.dialog.open(BlockerModalComponent, { data: options, disableClose: true });
  }

  /**
   * Opens a confirmation modal where user can choose between YES and NO options
   * @param popupOptions Popup options
   */
  openConfirmationModal(popupOptions?: Partial<ConfirmationModalPopupOptions>) {
    const options = popupOptions || {};
    defaults(options, this.defaults.popupOptions.confirmation);

    return this.dialog.open(ConfirmationModalComponent, { data: options });
  }

  /**
   * Opens a limitation modal (disabled feature due to plan)
   * @param translationPath Translation path where limitation-modal.title and limitation-modal.content will be taken
   */
  openLimitationModal(translationPath: string) {
    const options = {
      titleKey: `${translationPath}.limitation-modal.title`,
      descriptionKey: `${translationPath}.limitation-modal.content`
    };

    defaults(options, this.defaults.popupOptions.information);

    return this.dialog.open(InformationModalComponent, { data: options });
  }

  /**
   * Opens a confirmation modal and returns Promise<boolean> with resolve(true) when user clicks Discard and leave page
   * @param transition Transition object
   * @param justModal A flag when we want to ignore all guard checks and just show the modal
   */
  openNavigationAwayConfirmationModal(transition?: Transition, justModal?: boolean): Promise<boolean> {
    // There is a known bug in ui-router when uiCanExit calls 2 times
    // when redirecting to a route which state has a redirectTo param
    // https://github.com/angular-ui/ui-router/issues/3308
    // So we want to filter out 'second' opening the modal
    if (this.ignoreNextNavigationModalOpening && !justModal) {
      this.ignoreNextNavigationModalOpening = false;
      return Promise.resolve(true);
    }

    return new Promise((resolve, reject) => {
      this.openConfirmationModal({
        descriptionKey: 'common.page-leave-confirmation.description',
        titleKey: 'common.page-leave-confirmation.title',
        agreeButtonKey: 'common.page-leave-confirmation.agree',
        declineButtonKey: 'common.page-leave-confirmation.decline'
      })
        .afterClosed()
        .pipe(
          take(1),
          map(isConfirmed => !isConfirmed) // Reverse user selection
        )
        .subscribe(isConfirmed => {
          if (isConfirmed) {
            // For some case we use this method not in uiCanExit hook, but somewhere in a controller
            if (!justModal) {
              this.ignoreNextNavigationModalOpening = Boolean(transition.$to().redirectTo);
            }

            resolve(true);
          } else {
            reject();
          }
        });
    });
  }
}
